import { formatearFecha, rotuloPublicar } from './formato'

/**
 * El ultimo paso, y el unico que no se deshace: crea presupuestos en Odoo que
 * entran en el ciclo de reparto de las 06:00. Borrarlos no es cosa de esta
 * pantalla y un albaran de mas ya ha salido a la calle.
 *
 * Por eso el boton **dice cuantos clientes y que dia** en su propio rotulo, y no
 * en un texto de al lado que se puede no leer: es lo ultimo que se mira antes
 * de pulsar. Y por eso los que se quedan fuera se cuentan aqui: publicar
 * diecinueve de veinte sin enterarse del que falta es el fallo caro.
 */

interface Props {
  clientes: number
  /** Filas que no se publican porque les falta algo. Se dicen, no se esconden. */
  excluidos: number
  fecha: string
  /** Cambiar la fecha de reparto aquí mismo (antes solo se podía arriba). */
  onFecha: (fecha: string) => void
  publicando: boolean
  onPublicar: () => void
}

/** Hoy en ISO local. `toISOString()` daria UTC y al oeste de Greenwich, ayer. */
function hoyIso(): string {
  const ahora = new Date()
  const mes = String(ahora.getMonth() + 1).padStart(2, '0')
  const dia = String(ahora.getDate()).padStart(2, '0')
  return `${ahora.getFullYear()}-${mes}-${dia}`
}

export function BarraPublicar({ clientes, excluidos, fecha, onFecha, publicando, onPublicar }: Props) {
  const sinFecha = fecha === ''
  const enPasado = !sinFecha && fecha < hoyIso()
  const puede = clientes > 0 && !sinFecha && !publicando

  return (
    <div className="flex flex-col gap-3 rounded-xl border border-borde bg-superficie px-4 py-3">
      <div className="flex flex-col gap-1">
        <p className="text-sm text-tinta-2">
          {clientes === 0
            ? 'Ahora mismo no hay ningun cliente listo para publicar.'
            : `${clientes === 1 ? 'Se generará 1 albarán' : `Se generarán ${clientes} albaranes`} en Holded${
                sinFecha ? '' : ` con fecha de reparto ${formatearFecha(fecha)}`
              }. Esta accion no se deshace desde el panel.`}
        </p>
        {excluidos > 0 ? (
          <p className="text-sm text-atencion-tinta">
            {excluidos === 1
              ? 'Hay 1 fila que no se publicara: revisa su motivo en la tabla.'
              : `Hay ${excluidos} filas que no se publicaran: revisa sus motivos en la tabla.`}
          </p>
        ) : null}
        {sinFecha ? (
          <p className="text-sm text-atencion-tinta">
            Elige la fecha de reparto: es la que decide en que ruta entra el pedido.
          </p>
        ) : null}
        {enPasado ? (
          // Avisa y no bloquea, como el backend: puede haber que regularizar un
          // reparto ya hecho.
          <p className="text-sm text-atencion-tinta">
            La fecha de reparto es anterior a hoy. Se puede publicar igual, por si hay que
            regularizar un reparto ya hecho, pero comprueba que es la que quieres.
          </p>
        ) : null}
      </div>

      <div className="flex flex-wrap items-end gap-3">
        {/* La fecha se elige aquí mismo, en el último paso (antes solo arriba). */}
        <div className="flex flex-col gap-1">
          <label htmlFor="fecha-publicar" className="text-xs font-medium text-tinta-2">
            Fecha de reparto
          </label>
          <input
            id="fecha-publicar"
            type="date"
            value={fecha}
            onChange={(evento) => onFecha(evento.target.value)}
            className="rounded-lg border border-borde-fuerte bg-superficie px-3 py-2 text-sm text-tinta outline-none focus:border-foco focus:ring-2 focus:ring-foco-halo [color-scheme:dark]"
          />
        </div>

        <button
          type="button"
          disabled={!puede}
          onClick={onPublicar}
          className="rounded-lg bg-acento px-4 py-2 text-sm font-semibold text-tinta-inversa transition-colors hover:bg-acento-fuerte disabled:cursor-not-allowed disabled:bg-desactivado disabled:text-desactivado-tinta"
        >
          {publicando ? 'Publicando' : rotuloPublicar(clientes, fecha)}
        </button>
      </div>
    </div>
  )
}
